import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, Clock, Edit, Trash2, Loader2 } from "lucide-react";
import { CreateAppointmentModal } from "@/components/appointments/CreateAppointmentModal";

interface ActivitiesTabProps {
  caseId: string;
}

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration: number;
  status: string;
  priority: string;
  appointment_type: string;
  color_tag: string;
  location?: string;
  description?: string;
  prospect_name?: string;
  clients?: { name: string };
  cases?: { title: string };
  video_conference_link?: string;
}

const statusColors: Record<string, string> = {
  "Agendado": "bg-blue-500",
  "Confirmado": "bg-green-500", 
  "Em andamento": "bg-yellow-500",
  "Concluído": "bg-emerald-500",
  "Cancelado": "bg-red-500",
  "Reagendado": "bg-purple-500"
};

const appointmentTypeColors: Record<string, string> = {
  "audiencia": "bg-green-500",
  "prazo": "bg-yellow-500",
  "atendimento": "bg-blue-500",
  "outro": "bg-gray-500",
  "consulta": "bg-blue-500",
  "reuniao": "bg-green-700",
  "depoimento": "bg-purple-500",
  "pericia": "bg-orange-500",
  "mediacao": "bg-cyan-500",
  "conciliacao": "bg-lime-500",
  "diligencia": "bg-pink-500",
  "inspacao_judicial": "bg-slate-700",
  "sessao_julgamento": "bg-gray-800",
};

export const ActivitiesTab = ({ caseId }: ActivitiesTabProps) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (caseId) {
      fetchCaseAppointments(caseId);
    }
  }, [caseId]);

  const fetchCaseAppointments = async (id: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          clients:client_id (name),
          cases:case_id (title)
        `)
        .eq('case_id', id)
        .order('appointment_date', { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Erro ao buscar compromissos do processo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as atividades do processo.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    if (!window.confirm("Tem certeza que deseja excluir este compromisso?")) {
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', appointmentId);

      if (error) throw error;

      toast({
        title: "Sucesso!",
        description: "Compromisso excluído com sucesso."
      });
      fetchCaseAppointments(caseId); // Re-fetch appointments
    } catch (error) {
      console.error("Erro ao excluir compromisso:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o compromisso.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setShowEditModal(true);
  };

  const handleAppointmentUpdated = () => {
    fetchCaseAppointments(caseId); // Re-fetch appointments
    setShowEditModal(false);
    setEditingAppointment(null);
    toast({
      title: "Sucesso!",
      description: "Compromisso atualizado com sucesso."
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="mt-4 bg-white border border-[#c4ab83]/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="w-5 h-5" />
          Atividades Vinculadas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-96 pr-4">
          {appointments.length > 0 ? (
            <div className="space-y-4">
              {appointments.map((appointment) => (
                <div key={appointment.id} className="border rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-lg">{appointment.title}</h4>
                    <Badge className={`${statusColors[appointment.status]} text-white`}>
                      {appointment.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Clock className="w-4 h-4" />
                    <span>
                      {format(new Date(appointment.appointment_date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Calendar className="w-4 h-4" />
                    <Badge className={`${appointmentTypeColors[appointment.appointment_type]} text-white`}>
                      {appointment.appointment_type}
                    </Badge>
                  </div>
                  {appointment.description && (
                    <p className="text-sm text-muted-foreground mb-3">{appointment.description}</p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEditAppointment(appointment)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteAppointment(appointment.id)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nenhuma atividade vinculada a este processo.</p>
            </div>
          )}
        </ScrollArea>
      </CardContent>

      {editingAppointment && (
        <CreateAppointmentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          selectedDate={new Date(editingAppointment.appointment_date)}
          onAppointmentCreated={handleAppointmentUpdated}
          // Pass initial data for editing
          initialData={editingAppointment}
        />
      )}
    </Card>
  );
};